<template>
  <div v-if="props?.modelValue" class="campaign-card-wrapper">
    <div class="header">
      <img :src="imageSrc" alt="Campaign header" />
      <input type="file" id="header-image-input" accept="image/png, image/jpeg, image/gif" @change="editHeaderImage" ref="uploadInput" />
      <label for="header-image-input" class="btn secondary no-dark-mode">
        <i class="bi bi-pencil"></i>
      </label>
    </div>
    <div class="body">
      <div class="body-header">
        <div class="texts">
          <ConfirmInput class="headline" inputClass="sm white m-text" button-class="light outline" :placeholder="$t('app.campaign.card.campaignNamePlaceholder')" :modelValue="props.modelValue.campaign.headline" @update:model-value="editCampaignHeadline"></ConfirmInput>
          <ConfirmInput class="job-title" inputClass="sm white" button-class="light outline" :placeholder="$t('app.campaign.card.contextPlaceholder')" :modelValue="props.modelValue.campaign.job_title" @update:model-value="editCampaignJobTitle"></ConfirmInput>
        </div>
        <p class="minute-tag">{{ $t("game.assessmentCard.averageTimeTag", { duration }) }}</p>
      </div>
      <div class="tagline">
        <QuillEditor id="campaign-public-description" :content="props.modelValue.campaign.tagline" @update:content="taglineText = $event" @blur="editCampaignTagline" contentType="html" :options="quillOptions" />
      </div>
      <Button class="btn primary" :label="$t('app.campaign.card.buttons.start')"></Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEditCampaign } from '@/queries/campaigns.query';
import { useCampaignPublicInvitation } from '@/queries/invitation.query';
import { Campaign } from '@/types/campaign';
import { useToast } from 'vue-toastification';

import { QuillEditor } from '@vueup/vue-quill';
import ConfirmInput from '@/components/input/ConfirmInput.vue';

const toast = useToast();
const { t } = useI18n();
const { uploadImage } = useImageStore();
const appStore = useAppStore();
const { isCampaignV2, isCampaignV1, getAdventureDuration } = useSkillsStore();

const maxSize = 1024 * 2048; // 2Mo

const props = defineProps<{
  modelValue: Campaign
}>();

const emit = defineEmits<{
  (emit: "update:modelValue", value: Campaign): void;
}>();

const taglineText = ref('');

const quillOptions = computed(() => {
  return {
    theme: "snow",
    placeholder: t("app.campaign.card.descriptionPlaceholder"),
    modules: {
      toolbar: [["bold", "italic", "underline"], ["link"]],
    }
  }
});

const imageSrc = computed(() => {
  if (props?.modelValue?.branding?.banner_image_id) {
    return getImage(props?.modelValue?.branding?.banner_image_id);
  } else {
    return getRandomCampaignPicture(props?.modelValue?.campaign?.internal_name ?? "");
  }
});

const duration = computed<number>(() => {
  if (isCampaignV2(props?.modelValue)) {
    if (props?.modelValue?.variant === 'essentials') return 25;
    return 50;
  } else if (isCampaignV1(props?.modelValue)) {
    return 50;
  } else {
    return getAdventureDuration(props?.modelValue?.assessment?.modules);
  }
});

async function editHeaderImage(event: Event) {
  const el = event.target as HTMLInputElement;
  if (!el.files) return;
  const file = el.files[0];
  if (!file) return;

  // Check image format
  if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
    toast.error(t('app.campaign.newPage.slidingPanel.customization.imageWrongFormat'));
    return;
  }

  if (file.size > maxSize) {
    toast.error(t('app.campaign.newPage.slidingPanel.customization.imageTooLarge'));
    return;
  }

  try {
    // Upload image online
    const { image_id } = await uploadImage(appStore.currentOrganisationId, 'campaign_public_image', file);

    if (!image_id) {
      toast.error(t("app.campaign.single.imageUploadError"));
      return;
    }

    props.modelValue.branding.banner_image_id = image_id;
    emit('update:modelValue', props.modelValue);
  } catch (error) {
    toast.error(t('other.error.ERROR_UNKNOWN'));
  }
}

async function editCampaignHeadline(newHeadline: string) {
  props.modelValue.campaign.headline = newHeadline;
  emit('update:modelValue', props.modelValue);
}

async function editCampaignJobTitle(jobTitle: string) {
  props.modelValue.campaign.job_title = jobTitle;
  emit('update:modelValue', props.modelValue);
}

async function editCampaignTagline() {
  props.modelValue.campaign.tagline = taglineText.value;
  emit('update:modelValue', props.modelValue);
}

watch(props.modelValue, () => {
  taglineText.value = props?.modelValue?.campaign?.tagline ?? '';
});

onMounted(() => {
  taglineText.value = props?.modelValue?.campaign?.tagline ?? '';
});
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.campaign-card-wrapper { 
  margin: 16px 0;
  width: 500px;
  max-width: 600px;

  border: 2px solid #464646;
  border-radius: 14px;

  .header {
    position: relative;

    img {
      object-fit: cover;
      width: 100%;
      aspect-ratio: 3.5;
      border-radius: 12px 12px 0px 0px;
    }

    input {
      display: none;
    }

    label {
      position: absolute;
      bottom: 10px;
      right: 10px;
    }
  }
  
  .body {
    padding: 32px;
    background-color: $secondary-color;
    color: $white-color;
    border-radius: 0px 0px 12px 12px;

    .body-header {
      display: flex;
      gap: 50px;

      .texts {
        display: flex;
        flex-direction: column;
        gap: 4px;
        width: 100%;

        .headline { 
          width: 50%;
        }

        .job-title {
          width: 100%;
        }
      }

      .minute-tag {
        background-color: $primary-color;
        color: $black-color;
        font-size: 12px;
        padding: 2px 12px 4px;
        border-radius: 4px;
        width: fit-content;
        height: fit-content;
        display: flex;
        align-items: center;

        text-overflow: ellipsis;
        //overflow: hidden;
        white-space: nowrap;

      }
    }

    .tagline {
      margin-top: 12px;
      margin-bottom: 20px;
    }

    .btn {
      width: 100%;
    }
  }
}
</style>